<template>
  <div class="path-container" :title="filePath">
    <span v-if="directoryHtml"
          class="directories"
          v-html="directoryHtml"
    />
    <span class="file-name"
          v-html="fileNameHtml"
    />
  </div>
</template>

<script setup>
import { computed } from "vue"

const props = defineProps({
  filePath: { type: String, default: "" },
  searchHighlightStrings: { type: Array, default: null },
})

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function mergeRanges(ranges) {
  if (ranges.length === 0) {
    return []
  }
  const sorted = [...ranges].sort((a, b) => a[0] - b[0])
  const merged = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1]
    const cur = sorted[i]
    if (cur[0] <= prev[1]) {
      prev[1] = Math.max(prev[1], cur[1])
    } else {
      merged.push(cur)
    }
  }
  return merged
}

function highlightTermsInText(raw, terms) {
  if (!raw) {
    return ""
  }
  const uniqueTerms = [
    ...new Set(
      (terms || [])
        .map((t) => String(t).trim())
        .filter(Boolean)
    ),
  ]
  if (uniqueTerms.length === 0) {
    return escapeHtml(raw)
  }
  const ranges = []
  for (const term of uniqueTerms) {
    const re = new RegExp(escapeRegex(term), "gi")
    let m
    while ((m = re.exec(raw)) !== null) {
      ranges.push([m.index, m.index + m[0].length])
      if (m[0].length === 0) {
        break
      }
    }
  }
  const merged = mergeRanges(ranges)
  let out = ""
  let last = 0
  for (const [start, end] of merged) {
    out += escapeHtml(raw.slice(last, start))
    out += `<mark class="search-term-hit">${escapeHtml(raw.slice(start, end))}</mark>`
    last = end
  }
  out += escapeHtml(raw.slice(last))
  return out
}

const fileName = computed(() => (props.filePath || "").split("/").pop() || "")
const fileDirectoryPath = computed(() => {
  const pathDirectories = (props.filePath || "").split("/")
  pathDirectories.pop()
  return pathDirectories.length > 0 ? pathDirectories.join("/") + "/" : null
})

const directoryHtml = computed(() => {
  const dir = fileDirectoryPath.value
  if (!dir) {
    return null
  }
  return highlightTermsInText(dir, props.searchHighlightStrings)
})

const fileNameHtml = computed(() =>
  highlightTermsInText(fileName.value, props.searchHighlightStrings)
)
</script>

<style scoped lang="scss">
.path-container {
  display: flex;
  justify-content: flex-start;
  text-wrap: nowrap;

  overflow: hidden;
  text-overflow: ellipsis;

  .directories {
    flex: 0 999999 auto;
    overflow: hidden;
    text-overflow: '.../';
  }

  .file-name {
    flex: 0 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :deep(mark.search-term-hit) {
    background-color: #fff59d;
    color: inherit;
    padding: 0 0.05em;
  }
}
</style>
